import { useState, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CircleDot, FolderOpen, X } from 'lucide-react';
import { getTimelineBlockHeight, getTimelineBlockVisualKind, timeToMinutes, minutesToTime } from '../../lib/timeline';
import type { ColumnLayout } from '../../lib/timeline';
import { ResizeHandle } from '../Track/Timeline/ResizeHandle';
import { deleteTimelineBlock } from '../../hooks/useTimelineBlocks';
import { queryClient } from '../../api/queryClient';
import type { TimelineBlock } from '../../types';

interface PlannerBlockProps {
  block: TimelineBlock;
  startHour: number;
  pxPerHour: number;
  columnLayout?: ColumnLayout;
  onBlockClick?: (taskId: string) => void;
}

const priorityColors: Record<string, string> = {
  p1: 'bg-p1/12',
  p2: 'bg-p2/12',
  p3: 'bg-p3/12',
};

export function PlannerBlock({ block, startHour, pxPerHour, columnLayout, onBlockClick }: PlannerBlockProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `planner-block-${block.id}`,
    data: {
      type: 'planner-block',
      blockId: block.id,
      taskId: block.taskId,
      title: block.title,
      priority: block.priority,
      date: block.date,
      scheduledStartTime: block.scheduledStartTime,
      scheduledDuration: block.scheduledDuration,
    },
  });

  const [resizingDuration, setResizingDuration] = useState<number | null>(null);
  const didDrag = useRef(false);

  useEffect(() => {
    if (isDragging) didDrag.current = true;
  }, [isDragging]);

  const handleClick = () => {
    if (didDrag.current) { didDrag.current = false; return; }
    onBlockClick?.(block.taskId);
  };

  const effectiveDuration = resizingDuration ?? block.scheduledDuration;
  const startMin = timeToMinutes(block.scheduledStartTime);
  const endMin = startMin + effectiveDuration;
  const top = ((startMin - startHour * 60) * pxPerHour) / 60;
  const height = getTimelineBlockHeight(effectiveDuration, pxPerHour);
  const isShort = height < 36;
  const bg = priorityColors[block.priority] ?? 'bg-p2/12';
  const visualKind = block.parentId || block.itemType === 'project'
    ? getTimelineBlockVisualKind(block)
    : 'subtask';

  const col = columnLayout?.column ?? 0;
  const totalCols = columnLayout?.totalColumns ?? 1;
  const style: React.CSSProperties = {
    top,
    height,
    opacity: isDragging ? 0 : 1,
    zIndex: 10,
    ...(totalCols > 1 ? {
      left: `${(col / totalCols) * 100}%`,
      width: `${100 / totalCols}%`,
    } : {}),
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      className={`group absolute rounded-md overflow-visible select-none transition-shadow cursor-grab
        ${totalCols <= 1 ? 'left-0.5 right-0.5' : ''}
        ${bg}
        hover:shadow-sm`}
      style={style}
      title={`${block.title} (${minutesToTime(startMin)} – ${minutesToTime(endMin)})`}
    >
      <button
        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-70 bg-bg-surface/80 hover:bg-p1/20 text-text-muted hover:text-p1 transition-opacity z-10"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          deleteTimelineBlock(block.id, block.date);
          queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'range'] });
        }}
        title="Remove from timeline"
      >
        <X size={10} />
      </button>
      <div className={`h-full overflow-hidden px-2 ${isShort ? 'py-0.5 flex items-center gap-2' : 'py-1.5'}`}>
        <div className="flex items-center gap-1.5 min-w-0">
          {!block.parentId && block.itemType === 'project' && (
            <FolderOpen size={10} className="shrink-0" style={{ color: block.color ?? '#6366f1' }} />
          )}
          {!block.parentId && block.itemType !== 'project' && (
            <CircleDot size={10} className="shrink-0 text-accent" />
          )}
          <span className="text-micro font-medium text-text-primary truncate block">
            {block.title}
          </span>
        </div>
        {!isShort && (
          <span className="text-micro text-text-muted font-mono">
            {minutesToTime(startMin)} – {minutesToTime(endMin)}
          </span>
        )}
      </div>
      <ResizeHandle
        block={block}
        currentDuration={block.scheduledDuration}
        pxPerHour={pxPerHour}
        onResizing={setResizingDuration}
      />
    </div>
  );
}
